import { StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import widgetStyles from './styles/index.css?inline';
import { Widget } from './Widget';
import type { WidgetConfig, WidgetPosition } from './types/events';
import { initI18n } from '@i18n';

// ============================================================================
// Global State
// ============================================================================

let widgetRoot: Root | null = null;
let widgetContainer: HTMLDivElement | null = null;
let shadowRoot: ShadowRoot | null = null;

// ============================================================================
// Widget API
// ============================================================================

interface InitOptions {
  /** Channel ID (UUID) — preferred identifier for the widget */
  channelId?: string;
  /** Router ID (numeric) — legacy identifier, kept for backward compat */
  routerId?: string;
  apiHost?: string;
  wsHost?: string;
  position?: WidgetPosition;
  language?: string;
  direction?: 'ltr' | 'rtl';
}

function resolveOriginFromScript(script?: HTMLScriptElement): string | undefined {
  if (!script?.src) return undefined;
  try {
    return new URL(script.src, window.location.href).origin;
  } catch {
    return undefined;
  }
}

/**
 * Initialize the ClubPlanner chat widget.
 *
 * @param options - Configuration options for the widget
 * @param options.routerId - Required. The ID of the router to connect to.
 * @param options.apiHost - Optional. The API host URL. Defaults to VITE_API_HOST env var.
 * @param options.wsHost - Optional. The WebSocket host URL. Defaults to VITE_WS_HOST env var.
 * @param options.position - Optional. Widget position ('bottom-right' or 'bottom-left'). Defaults to 'bottom-right'.
 * @param options.language - Optional. Language code (e.g., 'en', 'nl', 'de').
 * @param options.direction - Optional. Layout direction ('ltr' or 'rtl'). Overrides the channel's
 *   setting; when omitted the channel decides, falling back to the language.
 */
async function init(options: InitOptions): Promise<void> {
  // Validate required options — need either channelId or routerId
  if (!options.channelId && !options.routerId) {
    console.error('[ClubPlannerWidget] channelId or routerId is required');
    return;
  }

  // Prevent double initialization
  if (widgetRoot) {
    console.warn('[ClubPlannerWidget] Widget already initialized. Call destroy() first to reinitialize.');
    return;
  }

  // Build configuration with defaults
  const apiHost =
    options.apiHost
    ?? resolveOriginFromScript(document.currentScript as HTMLScriptElement | null ?? undefined)
    ?? import.meta.env.VITE_API_HOST
    ?? 'http://localhost:8000';
  const wsHostFromApi = apiHost.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
  const config: WidgetConfig = {
    channelId: options.channelId,
    routerId: options.routerId || options.channelId || '',
    apiHost,
    wsHost: options.wsHost ?? import.meta.env.VITE_WS_HOST ?? wsHostFromApi,
    position: options.position,
    language: options.language,
    direction: options.direction === 'ltr' || options.direction === 'rtl' ? options.direction : undefined,
  };

  // Create host element for the shadow DOM
  widgetContainer = document.createElement('div');
  widgetContainer.id = 'clubplanner-widget-container';
  document.body.appendChild(widgetContainer);

  // Attach shadow DOM for complete CSS isolation from host page
  shadowRoot = widgetContainer.attachShadow({ mode: 'open' });

  // Inject widget styles into shadow root (isolated from host page)
  const styleEl = document.createElement('style');
  styleEl.textContent = widgetStyles;
  shadowRoot.appendChild(styleEl);

  // Create render target inside shadow root
  const renderTarget = document.createElement('div');
  shadowRoot.appendChild(renderTarget);

  // Initialize i18n before rendering
  await initI18n(options.language);

  // Create React root and render inside shadow DOM
  widgetRoot = createRoot(renderTarget);
  widgetRoot.render(
    <StrictMode>
      <Widget config={config} />
    </StrictMode>
  );

  console.log('[ClubPlannerWidget] Initialized with', config.channelId ? `channel: ${config.channelId}` : `router: ${config.routerId}`);
}

/**
 * Destroy the widget and clean up resources.
 * Call this before reinitializing with different options.
 */
function destroy(): void {
  if (widgetRoot) {
    widgetRoot.unmount();
    widgetRoot = null;
  }

  if (widgetContainer) {
    widgetContainer.remove();
    widgetContainer = null;
    shadowRoot = null;
  }

  console.log('[ClubPlannerWidget] Destroyed');
}

// ============================================================================
// Global API Object
// ============================================================================

const ClubPlannerWidget = {
  init,
  destroy,
};

// Export to window for UMD builds
declare global {
  interface Window {
    ClubPlannerWidget: typeof ClubPlannerWidget;
  }
}

window.ClubPlannerWidget = ClubPlannerWidget;

// ============================================================================
// Auto-initialization from script data attributes
// ============================================================================

function autoInit(): void {
  // Find the script tag that loaded this widget (support both channel-id and router-id)
  const scripts = document.querySelectorAll('script[data-channel-id], script[data-router-id]');
  const currentScript = scripts[scripts.length - 1] as HTMLScriptElement | undefined;

  if (!currentScript) {
    // No auto-init data attributes found
    return;
  }

  const channelId = currentScript.dataset.channelId;
  const routerId = currentScript.dataset.routerId;
  if (!channelId && !routerId) {
    return;
  }

  const options: InitOptions = {
    channelId,
    routerId,
    apiHost: currentScript.dataset.apiHost || resolveOriginFromScript(currentScript),
    wsHost: currentScript.dataset.wsHost,
    position: currentScript.dataset.position as WidgetPosition | undefined,
    language: currentScript.dataset.language,
    direction: currentScript.dataset.direction as 'ltr' | 'rtl' | undefined,
  };

  // Auto-initialize
  init(options);
}

// Run auto-init when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInit);
} else {
  // DOM already loaded, run immediately
  autoInit();
}

// Export for ES modules (if needed)
export { init, destroy, ClubPlannerWidget };
export default ClubPlannerWidget;
